/**
 * Validation utilities for form validation
 */

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * Validates email format
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validates required field
 */
export function validateRequired(value: any, fieldName: string): string | null {
  if (value === null || value === undefined || value === "") {
    return `${fieldName} is required`;
  }
  return null;
}

/**
 * Validates minimum length
 */
export function validateMinLength(
  value: string,
  minLength: number,
  fieldName: string,
): string | null {
  if (value.length < minLength) {
    return `${fieldName} must be at least ${minLength} characters`;
  }
  return null;
}

/**
 * Validates maximum length
 */
export function validateMaxLength(
  value: string,
  maxLength: number,
  fieldName: string,
): string | null {
  if (value.length > maxLength) {
    return `${fieldName} must be no more than ${maxLength} characters`;
  }
  return null;
}

/**
 * Validates content item
 */
export function validateContentItem(data: {
  title?: string;
  body?: string;
  kind?: string;
}): ValidationResult {
  const errors: ValidationError[] = [];

  if (data.title !== undefined) {
    const titleError = validateRequired(data.title, "Title");
    if (titleError) errors.push({ field: "title", message: titleError });

    if (data.title) {
      const minLengthError = validateMinLength(data.title, 3, "Title");
      if (minLengthError) {
        errors.push({ field: "title", message: minLengthError });
      }

      const maxLengthError = validateMaxLength(data.title, 200, "Title");
      if (maxLengthError) {
        errors.push({ field: "title", message: maxLengthError });
      }
    }
  }

  if (data.body !== undefined) {
    const bodyError = validateRequired(data.body, "Body");
    if (bodyError) errors.push({ field: "body", message: bodyError });

    if (data.body) {
      const minLengthError = validateMinLength(data.body, 10, "Body");
      if (minLengthError) {
        errors.push({ field: "body", message: minLengthError });
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates login credentials
 */
export function validateLogin(data: {
  email: string;
  password: string;
}): ValidationResult {
  const errors: ValidationError[] = [];

  const emailError = validateRequired(data.email, "Email");
  if (emailError) {
    errors.push({ field: "email", message: emailError });
  } else if (!validateEmail(data.email)) {
    errors.push({ field: "email", message: "Email is invalid" });
  }

  const passwordError = validateRequired(data.password, "Password");
  if (passwordError) {
    errors.push({ field: "password", message: passwordError });
  } else if (data.password.length < 6) {
    errors.push({
      field: "password",
      message: "Password must be at least 6 characters",
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates schedule
 */
export function validateSchedule(data: {
  name?: string;
  cron?: string;
  jobType?: string;
}): ValidationResult {
  const errors: ValidationError[] = [];

  if (data.name !== undefined) {
    const nameError = validateRequired(data.name, "Schedule name");
    if (nameError) errors.push({ field: "name", message: nameError });

    if (data.name) {
      const minLengthError = validateMinLength(data.name, 3, "Schedule name");
      if (minLengthError)
        errors.push({ field: "name", message: minLengthError });

      const maxLengthError = validateMaxLength(data.name, 100, "Schedule name");
      if (maxLengthError)
        errors.push({ field: "name", message: maxLengthError });
    }
  }

  if (data.cron !== undefined) {
    const cronError = validateRequired(data.cron, "Cron expression");
    if (cronError) errors.push({ field: "cron", message: cronError });

    // Basic cron validation (5 or 6 fields)
    if (data.cron) {
      const cronParts = data.cron.trim().split(/\s+/);
      if (cronParts.length < 5 || cronParts.length > 6) {
        errors.push({
          field: "cron",
          message: `Cron expression must have 5 or 6 fields separated by spaces. You entered ${cronParts.length} field(s). Example: "0 9 * * *" (5 fields) or "0 0 9 * * *" (6 fields)`,
        });
      }
    }
  }

  if (data.jobType !== undefined) {
    const jobTypeError = validateRequired(data.jobType, "Job type");
    if (jobTypeError) errors.push({ field: "jobType", message: jobTypeError });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}
